import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { oidcClient } from '@/lib/oidc-client'
import { OIDCClient, retrievePKCEVerifier } from '@agion/oidc-client'
import { config } from '@/utils/config'
import { authService } from '@/services/auth'
import { resolveAuthenticationContinuation } from '@/utils/auth-continuation'
import { readSessionHomeUrl } from '@/services/session-home'
import { homeAfterAcceptance } from '@/services/invitation-landing'
import { Logo } from '@/components/common/Logo'

function resolveReturnToRedirect(returnTo: string | null): string | null {
  if (!returnTo) return null
  try {
    return resolveAuthenticationContinuation(returnTo, config.authServiceUrl)
  } catch {
    return null
  }
}

export const CallbackPage = () => {
  const { t } = useTranslation()
  const [error, setError] = useState<string>('')
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    const handleCallback = async () => {
      try {
        // Parse authorization callback from URL
        const { code, state } = OIDCClient.parseAuthorizationCallback(window.location.href)
        window.history.replaceState(window.history.state, '', window.location.pathname)

        if (!code || !state) {
          throw new Error('Missing authorization code or state parameter')
        }

        // Validate state (CSRF protection)
        const storedState = sessionStorage.getItem('oidc_state')
        if (!storedState || !OIDCClient.validateState(state, storedState)) {
          throw new Error('Invalid state parameter - possible CSRF attack')
        }

        // Retrieve PKCE verifier
        const verifier = retrievePKCEVerifier(state)
        if (!verifier) {
          throw new Error('PKCE verifier not found - possible session issue')
        }

        // STEP 1: Exchange authorization code for tokens (ID token, access token)
        // This is standard OIDC Authorization Code Flow with PKCE
        await oidcClient.exchangeCodeForTokens(
          code,
          config.redirectUri,
          verifier
        )

        // NOTE: The agion_sso_session cookie was already set by auth-service during the /authorize flow.
        // No need for an additional session exchange - the SSO cookie is sufficient for authentication.
        // The target application (admin-ui, chat-ui, etc.) will validate the SSO session when it loads.

        // STEP 2: Accept pending invitation if one exists
        // This handles the case where user clicked an invitation link, logged in, and now needs to join the org
        // An accepted invitation names the organisation it joined and that
        // membership's home (ADR-20260930 D7): the session moves there, then,
        // without a stored continuation, the browser lands on that home.
        let acceptedHome: string | null = null
        const invitationToken = sessionStorage.getItem('invitation_token')
        if (invitationToken) {
          try {
            const acceptance = await authService.acceptInvitation(invitationToken)
            acceptedHome = await homeAfterAcceptance(acceptance, (id) => authService.switchOrganization(id))
          } catch {
            console.warn('[auth] legacy_callback_invitation_failed')
            // Don't throw - continue with redirect
          } finally {
            sessionStorage.removeItem('invitation_token')
          }
        }

        // STEP 3: Determine redirect target
        // First check for a return_to path (e.g., /device?user_code=XXXX from device code flow).
        // Otherwise land on the member's home as auth-service decides it
        // (ADR-20260930 D7); Login UI never derives an app address.
        const returnTo = sessionStorage.getItem('return_to')
        sessionStorage.removeItem('oidc_state')
        sessionStorage.removeItem('return_to')
        sessionStorage.removeItem('client_id')
        const redirectUrl = resolveReturnToRedirect(returnTo)
          ?? resolveReturnToRedirect(acceptedHome)
          ?? resolveReturnToRedirect(await readSessionHomeUrl(config.authServiceUrl))
        if (!redirectUrl) {
          throw new Error('No landing address')
        }

        // STEP 4: Redirect back to the originating application
        // The agion_session cookie will be sent automatically (same domain .agion.dev)
        window.location.href = redirectUrl
      } catch {
        window.history.replaceState(window.history.state, '', window.location.pathname)
        console.warn('[auth] legacy_callback_rejected')
        setError('callback_failed')
      }
    }

    handleCallback()
  }, [])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <Logo className="h-12 mx-auto mb-6" theme="auto" />

        {error ? (
          <div className="mb-6">
            <div className="p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg mb-4">
              <p role="alert" className="text-error-600 dark:text-error-400">
                {t('signInRecovery.message', { defaultValue: 'We could not complete this sign-in. Please sign in again to continue.' })}
              </p>
            </div>
            <Link to="/login" replace className="inline-flex rounded-lg bg-accent-600 px-5 py-3 font-medium text-white hover:bg-accent-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600">
              {t('signInRecovery.action', { defaultValue: 'Sign in again' })}
            </Link>
          </div>
        ) : (
          <div className="mb-6">
            <div className="flex justify-center mb-4">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-accent-600 border-t-transparent"></div>
            </div>
            <p className="text-gray-600 dark:text-gray-400">{t('signInRecovery.processing', { defaultValue: 'Completing sign-in…' })}</p>
          </div>
        )}
      </div>
    </div>
  )
}
